import {
    FC,
    useMemo,
    useRef,
    CSSProperties,
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
} from 'react';
import classnames from 'classnames';
import { useIntl, defineMessages } from 'dibs-react-intl';
import ZoomIn from 'dibs-icons/exports/legacy/ZoomIn';
import ZoomOut from 'dibs-icons/exports/legacy/ZoomOut';
import { useFullscreen } from 'dibs-elements/exports/useFullscreen';
import { ItemMediaSpinner } from 'dibs-elements/exports/ItemMediaSpinner';

import styles from './NftPdpHtmlViewer.scss';

const messages = defineMessages({
    fullScreen: { id: 'NFT.Html.fullScreen', defaultMessage: 'Full screen' },
    exitFullScreen: {
        id: 'NFT.Html.exitFullScreen',
        defaultMessage: 'Exit full screen',
    },
});

type Props = {
    src: string | null;
    poster: string;
    isMobile: boolean;
    aspectRatio?: number | 'auto' | null;
};
type IFrameState = 'resize' | 'remove' | 'load' | 'ready';

const useIsomorphicEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// safari may resize more than once during a fullscreen transition--throttle to
// prevent extra iframe rerenders
const FULLSCREEN_TIMEOUT = 2000;
// allow non-fullscreen resize events more frequently
const RESIZE_TIMEOUT = 250;

function useResizeIFrame(): [IFrameState, () => void] {
    const [resizeState, setResizeState] = useState<IFrameState>('remove');

    useEffect(() => {
        let lastResize = 0;
        let lastFullscreen = 0;
        let wasFullscreen = false;

        function onResize(): void {
            const now = performance.now();
            const isFullscreen = !!(
                document.fullscreenElement ||
                // @ts-expect-error
                document.webkitFullscreenElement
            );
            const fullscreenChange = wasFullscreen !== isFullscreen;
            if (fullscreenChange) {
                lastFullscreen = now;
                wasFullscreen = isFullscreen;
            }
            const allowThrottled =
                now - lastFullscreen > FULLSCREEN_TIMEOUT && now - lastResize > RESIZE_TIMEOUT;
            // resize iframe on debounced resize events or resize events where
            // the fullscreen state has changed
            if (fullscreenChange || allowThrottled) {
                lastResize = now;
                setResizeState('resize');
            }
        }
        window.addEventListener('resize', onResize, false);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // useLayoutEffect bc it is necessary to ensure each state is rendered at
    // least once before transitioning
    useIsomorphicEffect(() => {
        switch (resizeState) {
            case 'resize':
                setResizeState('remove');
                break;
            case 'remove':
                setResizeState('load');
                break;
        }
    }, [resizeState]);

    const onIFrameLoad = useCallback(() => setResizeState('ready'), []);

    return [resizeState, onIFrameLoad];
}

export const NftPdpHtmlViewer: FC<Props> = ({ src, isMobile, aspectRatio, poster }) => {
    aspectRatio ??= 'auto';
    const { formatMessage } = useIntl();
    const iframeContainerRef = useRef<HTMLDivElement>(null);
    const spinnerKeyRef = useRef(0);
    const { isFullscreen, handleFullscreenToggle } = useFullscreen(iframeContainerRef, true);
    const [loadingState, onIFrameLoad] = useResizeIFrame();
    const ariaLabel = useMemo(() => {
        return formatMessage(isFullscreen ? messages.exitFullScreen : messages.fullScreen);
    }, [formatMessage, isFullscreen]);
    const cssProperties = {
        '--html-viewer-aspect-ratio': aspectRatio,
        '--poster': poster ? `url(${poster})` : undefined,
    } as CSSProperties;

    if (loadingState === 'resize') {
        // ItemMediaSpinner auto unmounts; rerender it with a new key
        spinnerKeyRef.current++;
    }
    // remove iframe to get the HTML NFT to re-render. other methods of
    // reloading an iframe add to the browser history, which creates bad UX for
    // the back button
    const MaybeIFrame = loadingState === 'remove' ? 'div' : 'iframe';
    return (
        <div ref={iframeContainerRef} style={cssProperties} className={styles.media}>
            <MaybeIFrame
                onLoad={onIFrameLoad}
                loading="lazy"
                sandbox="allow-same-origin allow-scripts"
                allow="fullscreen; accelerometer; camera; gyroscope; microphone; xr-spatial-tracking;"
                title="html"
                className={classnames(styles.iframe, {
                    [styles.loading]: loadingState !== 'ready',
                    [styles.landscape]: (aspectRatio as number) > 1,
                    [styles.portrait]: (aspectRatio as number) < 1,
                    [styles.auto]: aspectRatio === 'auto',
                })}
                src={src || ''}
            />
            <ItemMediaSpinner key={spinnerKeyRef.current} isOpen={loadingState !== 'ready'} />
            {!isMobile && (
                <div className={styles.controlList}>
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <button
                        type="button"
                        title={ariaLabel}
                        onClick={handleFullscreenToggle}
                        aria-label={ariaLabel}
                        className={styles.control}
                    >
                        {isFullscreen ? <ZoomOut /> : <ZoomIn />}
                    </button>
                </div>
            )}
        </div>
    );
};
